// Voice helpers: mic recording + STT (Whisper) and TTS playback (OpenAI) via the backend.
import api from "@/lib/api";

let _audio = null; // currently-playing HTMLAudioElement (so we can stop it)

export function supportsMic() {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
}

// Records from the mic until stop() is called; returns a controller with stop() -> transcript.
export async function startRecording() {
  if (!supportsMic()) throw new Error("Microphone not supported in this browser.");
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
    : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
  rec.start();
  return {
    stop: () => new Promise((resolve, reject) => {
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        try {
          const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
          if (!blob.size) return resolve("");
          resolve(await transcribe(blob));
        } catch (e) { reject(e); }
      };
      rec.stop();
    }),
    cancel: () => { try { rec.onstop = () => stream.getTracks().forEach((t) => t.stop()); rec.stop(); } catch (e) {} },
  };
}

export async function transcribe(blob) {
  const fd = new FormData();
  const ext = (blob.type || "").includes("mp4") ? "mp4" : "webm";
  fd.append("audio", blob, `speech.${ext}`);
  const { data } = await api.post("/voice/stt", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 45000 });
  return (data && data.text) || "";
}

export async function speak(text, voice = "nova") {
  if (!text || !String(text).trim()) return;
  try {
    stopSpeaking();
    const { data } = await api.post("/voice/tts", { text: String(text).slice(0, 1200), voice }, { timeout: 45000 });
    if (!data || !data.audio) return;
    const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
    _audio = audio;
    await audio.play().catch(() => {});
  } catch (e) { /* voice is best-effort; never block the UI */ }
}

export function stopSpeaking() {
  if (_audio) { try { _audio.pause(); _audio.currentTime = 0; } catch (e) {} _audio = null; }
}
